import { Alert, FormControlLabel, Stack, Switch, Typography } from "@mui/material"
import { If } from "lib/@components/switch"

/**
 * The force option (FIQ-1926).
 *
 * Two switches, deliberately not one, because the two bypasses are not equally dangerous:
 *
 *  - roleFilter overrides the CLIENT's own delegation. Notable, but an ordinary staff action.
 *  - serverDemands overrides an ENVIRONMENT safety gate, making the override more powerful
 *    than actually buying the product. That is gated on full `admin` — the same permission
 *    that gates editing the server_demands setting itself. Same power, same gate.
 *
 * Both require an expiry (enforced here and again on the server), because an indefinite
 * bypass of a safety gate is precisely what accumulates unnoticed.
 */
export function BypassControls({ grant, onChange, canForceServerDemands }) {
    const bypass = grant.bypass ?? {}

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2">Force</Typography>
            <Typography variant="caption" color="text.secondary">
                An override already beats DemandAudit. These force it past the two stages it does not beat by default.
                Use sparingly.
            </Typography>

            <FormControlLabel
                control={<Switch checked={!!bypass.roleFilter} onChange={toggle("roleFilter")} />}
                label="Force past the role filter (ignore the client's role assignment)"
                data-cy="grant-bypass-roleFilter"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={!!bypass.serverDemands}
                        onChange={toggle("serverDemands")}
                        disabled={!canForceServerDemands}
                    />
                }
                label="Force past the environment feature gate (ignore server_demands)"
                data-cy="grant-bypass-serverDemands"
            />

            <If value={!canForceServerDemands}>
                <Typography variant="caption" color="text.secondary">
                    Forcing past the environment gate requires the full <strong>admin</strong> permission, because it is
                    the same power as editing the server_demands setting.
                </Typography>
            </If>

            <If value={!!bypass.roleFilter || !!bypass.serverDemands}>
                <Alert severity="warning" data-cy="grant-bypass-warning">
                    This override forces a demand past a filter that would otherwise remove it. It must have an expiry
                    date, and it will be written to the audit log.
                </Alert>
            </If>
        </Stack>
    )

    function toggle(stage) {
        return (e) => onChange((current) => ({ ...current, bypass: { ...current.bypass, [stage]: e.target.checked } }))
    }
}
